import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { BarChart3, RefreshCw, Calendar } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { tiktokService, TiktokDailyRow } from '@/services/tiktok.service';
import toast from 'react-hot-toast';

type Level = 'campaign' | 'adgroup' | 'ad';
type Metric = 'impressions' | 'clicks' | 'spend' | 'conversion' | 'reach' | 'videoPlayActions';

const METRIC_LABELS: Record<Metric, string> = {
  impressions:      'Impressions',
  clicks:           'Clicks',
  spend:            'Spend',
  conversion:       'Conversions',
  reach:            'Reach',
  videoPlayActions: 'Video views',
};

interface Props {
  open: boolean;
  onClose: () => void;
  level: Level;
  entityId: string;
  days: number;
  /** Total row count synced (header shows that fewer rows than `days` may be present). */
  totalsHint?: { impressions: number; clicks: number; spend: number; conversion: number };
}

function ymd(d: Date): string { return d.toISOString().slice(0, 10); }

/** Pure SVG bar chart. Axis labels rendered as a <text> row below. */
const DailyBars: React.FC<{ rows: TiktokDailyRow[]; metric: Metric }> = ({ rows, metric }) => {
  const values = rows.map(r => Number(r[metric]) || 0);
  const max = Math.max(...values, 1);
  const W = 820;
  const H = 180;
  const barW = rows.length > 0 ? W / rows.length : 0;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 22}`} className="block">
      {/* Bars */}
      {values.map((v, i) => {
        const h = max > 0 ? (v / max) * (H - 8) : 0;
        return (
          <g key={i}>
            <rect
              x={i * barW + 2}
              y={H - h}
              width={Math.max(barW - 4, 1)}
              height={h}
              fill="currentColor"
              className="text-primary"
              opacity={0.7 + 0.3 * (v / max)}
              rx={2}
            >
              <title>{`${rows[i].date}: ${v.toLocaleString()}`}</title>
            </rect>
            {/* Top value label for non-zero bars when there's room */}
            {v > 0 && barW > 36 && (
              <text
                x={i * barW + barW / 2}
                y={H - h - 3}
                fontSize="9"
                textAnchor="middle"
                className="fill-text-muted tabular-nums"
              >
                {metric === 'spend' ? v.toFixed(2) : v.toLocaleString()}
              </text>
            )}
            {/* Date label */}
            <text
              x={i * barW + barW / 2}
              y={H + 14}
              fontSize="9"
              textAnchor="middle"
              className="fill-text-muted tabular-nums"
            >
              {rows[i].date.slice(5)}
            </text>
          </g>
        );
      })}
      {/* X-axis baseline */}
      <line x1={0} y1={H} x2={W} y2={H} className="stroke-border" strokeWidth={1} />
    </svg>
  );
};

export const DailyBreakdownModal: React.FC<Props> = ({ open, onClose, level, entityId, days, totalsHint }) => {
  const [rows, setRows] = useState<TiktokDailyRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [metric, setMetric] = useState<Metric>('impressions');

  const load = async () => {
    setLoading(true);
    try {
      const from = ymd(new Date(Date.now() - days * 86400_000));
      const to = ymd(new Date());
      const r = await tiktokService.dailyReport({ level, entityId, from, to });
      setRows(r);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };

  // Reload whenever the modal opens or the entity / range changes.
  useEffect(() => {
    if (!open) return;
    setRows(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId, level, days]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await tiktokService.syncReport({ daysBack: days, level });
      toast.success(`Synced ${r.upserted} rows over ${r.days} days`);
      try { localStorage.setItem('slightercrm.tiktok.report.syncedAt', new Date().toISOString()); } catch { /* quota */ }
      await load();
    } catch {
      // axios interceptor toasts the backend message already.
    } finally { setSyncing(false); }
  };

  const totals = useMemo(() => {
    const t = { impressions: 0, clicks: 0, spend: 0, conversion: 0, reach: 0, videoPlayActions: 0 };
    (rows ?? []).forEach((r) => {
      t.impressions += r.impressions;
      t.clicks += r.clicks;
      t.spend += r.spend;
      t.conversion += r.conversion;
      t.reach += r.reach;
      t.videoPlayActions += r.videoPlayActions;
    });
    return t;
  }, [rows]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Daily breakdown · ${level} ${entityId}`}
      widthClass="max-w-4xl"
    >
      <div className="space-y-4">
        {/* Header / sync */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-text-muted">
            Showing the last <strong className="text-text-primary">{days}</strong> days from your local cache.
            {totalsHint && (
              <span> Totals match the row in the table — re-sync if numbers look stale.</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5 text-2xs">
              {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => {
                const active = metric === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMetric(m)}
                    className={clsx(
                      'rounded-md px-2 py-1 font-medium transition-all',
                      active ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
                    )}
                  >
                    {METRIC_LABELS[m]}
                  </button>
                );
              })}
            </div>
            <Button variant="secondary" size="sm" loading={syncing} onClick={sync}>
              <RefreshCw size={12} /> Re-sync
            </Button>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {[
            { label: 'Impressions', value: totals.impressions.toLocaleString() },
            { label: 'Clicks',      value: totals.clicks.toLocaleString() },
            { label: 'Spend',       value: totals.spend.toFixed(2) },
            { label: 'Conv',        value: totals.conversion.toLocaleString() },
            { label: 'Reach',       value: totals.reach.toLocaleString() },
            { label: 'Video views', value: totals.videoPlayActions.toLocaleString() },
          ].map((k) => (
            <div key={k.label} className="rounded-xl border border-border bg-surface-alt/40 p-2 text-center">
              <div className="text-2xs uppercase tracking-wide text-text-muted">{k.label}</div>
              <div className="mt-0.5 font-display text-base font-bold text-text-primary tabular-nums">{k.value}</div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="rounded-2xl border border-border bg-surface p-4">
          {loading && <div className="skeleton h-44 rounded-lg" />}
          {!loading && rows && rows.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <BarChart3 size={28} className="text-text-muted" />
              <p className="text-sm font-semibold text-text-primary">No daily rows in your local cache</p>
              <p className="text-xs text-text-muted max-w-sm">
                Either this entity hasn't run yet, or you haven't synced. Click <strong>Re-sync</strong> above
                to pull the latest {days} days from TikTok into your DB.
              </p>
            </div>
          )}
          {!loading && rows && rows.length > 0 && (
            <>
              <div className="mb-2 flex items-center gap-2">
                <BarChart3 size={14} className="text-primary" />
                <span className="text-xs font-semibold text-text-primary">
                  Daily {METRIC_LABELS[metric]} · {rows[0].date} → {rows[rows.length - 1].date}
                </span>
              </div>
              <DailyBars rows={rows} metric={metric} />
            </>
          )}
        </div>

        {/* Per-day table */}
        {rows && rows.length > 0 && (
          <div className="rounded-2xl border border-border bg-surface overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-surface-alt/40">
              <Calendar size={12} className="text-text-muted" />
              <span className="text-2xs uppercase tracking-wide font-semibold text-text-muted">Per-day rows from your DB</span>
            </div>
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border bg-surface text-2xs uppercase tracking-wide text-text-muted sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Day</th>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-right">Impr</th>
                    <th className="px-4 py-2 text-right">Clicks</th>
                    <th className="px-4 py-2 text-right">CTR</th>
                    <th className="px-4 py-2 text-right">Spend</th>
                    <th className="px-4 py-2 text-right">Conv</th>
                    <th className="px-4 py-2 text-right">Reach</th>
                    <th className="px-4 py-2 text-right">Video views</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r, i) => {
                    const ctr = r.impressions > 0 ? (r.clicks / r.impressions * 100).toFixed(2) : '-';
                    return (
                      <tr key={r.date} className="hover:bg-surface-alt">
                        <td className="px-4 py-2 font-mono text-text-muted tabular-nums">Day {i + 1}</td>
                        <td className="px-4 py-2 font-mono text-text-primary tabular-nums">{r.date}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.impressions.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.clicks.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{ctr}{ctr !== '-' && '%'}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.spend.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.conversion.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.reach.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.videoPlayActions.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
